import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Sparkles } from 'lucide-react';
import { useGameStore } from '@/store';
import { logger } from '@/utils/logger';
import type { GameType } from '@party-game/shared';

// Word categories
const CATEGORIES = [
  { id: 'all', name: 'كل التصنيفات', emoji: '🎲' },
  { id: 'places', name: 'أماكن', emoji: '📍' },
  { id: 'food', name: 'طعام', emoji: '🍕' },
  { id: 'animals', name: 'حيوانات', emoji: '🦁' },
  { id: 'jobs', name: 'مهن', emoji: '👨‍⚕️' },
  { id: 'sports', name: 'رياضة', emoji: '⚽' },
];

// API URL - use port 8787 in development, Cloudflare Workers in production
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_URL = isDev 
  ? 'http://localhost:8787' 
  : (import.meta.env.VITE_API_URL || 'https://party-game-api.ibrahem-magdy025.workers.dev');

export function CreateRoomPage() {
  const navigate = useNavigate();
  const { setPlayer, setRoom, setIsHost, setPlayers, setGameSettings } = useGameStore();
  
  const [hostName, setHostName] = useState('');
  const [gameType, setGameType] = useState<GameType>('spy');
  const [spyCount, setSpyCount] = useState(1);
  const [category, setCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Fixed max players at 10
  const maxPlayers = 10;

  const handleCreate = async () => {
    if (!hostName.trim()) {
      setError('الرجاء إدخال اسمك');
      return;
    }

    setIsLoading(true);
    setError('');

    logger.info('CreateRoom', 'Creating room', { hostName, gameType, maxPlayers, spyCount, category });

    try {
      const response = await fetch(`${API_URL}/api/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hostName: hostName.trim(),
          gameType,
          maxPlayers,
          spyCount: gameType === 'spy' ? spyCount : undefined,
          category: gameType === 'spy' ? category : undefined,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'فشل في إنشاء الغرفة');
      }

      // Store player and room info
      const newPlayer = {
        id: data.data.player.id,
        roomId: data.data.room.id,
        name: hostName.trim(),
        score: 0,
        role: 'host' as const,
        isConnected: true,
        joinedAt: new Date().toISOString(),
      };
      
      setPlayer(newPlayer);
      setPlayers([newPlayer]); // Add player to players list
      
      setRoom({
        id: data.data.room.id,
        code: data.data.room.code,
        hostId: data.data.player.id,
        gameType: data.data.room.gameType,
        status: 'waiting',
        currentRound: 0,
        maxPlayers,
        createdAt: new Date().toISOString(),
      });
      
      // Store game settings
      setGameSettings({
        spyCount: gameType === 'spy' ? spyCount : 1,
        category: gameType === 'spy' ? category : 'all',
      });
      
      setIsHost(true);

      // Navigate to lobby
      navigate(`/lobby/${data.data.room.code}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ غير متوقع');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          العودة للرئيسية
        </button>

        <div className="card">
          <h1 className="text-3xl font-bold mb-2 text-center">إنشاء غرفة جديدة</h1>
          <p className="text-white/60 text-center mb-8">اختر نوع اللعبة وابدأ المتعة!</p>

          {/* Host name */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">اسمك</label>
            <input
              type="text"
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              placeholder="أدخل اسمك..."
              className="input"
              maxLength={20}
            />
          </div>

          {/* Game type */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">نوع اللعبة</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setGameType('spy')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  gameType === 'spy'
                    ? 'border-game-accent bg-game-accent/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-3xl mb-2 block">🕵️</span>
                <span className="font-bold">الجاسوس</span>
                <p className="text-xs text-white/60 mt-1">اكتشف الجاسوس!</p>
              </button>
              <button
                onClick={() => setGameType('quiz')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  gameType === 'quiz'
                    ? 'border-primary-500 bg-primary-500/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <span className="text-3xl mb-2 block">❓</span>
                <span className="font-bold">أسئلة</span>
                <p className="text-xs text-white/60 mt-1">اختبر معلوماتك!</p>
              </button>
            </div>
          </div>

          {/* Spy Count - Only for spy game */}
          {gameType === 'spy' && (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  🕵️ عدد الجواسيس: {spyCount}
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSpyCount(1)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      spyCount === 1
                        ? 'border-game-accent bg-game-accent/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <span className="text-2xl block mb-1">🕵️</span>
                    <span className="font-bold">جاسوس واحد</span>
                  </button>
                  <button
                    onClick={() => setSpyCount(2)}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      spyCount === 2
                        ? 'border-game-accent bg-game-accent/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <span className="text-2xl block mb-1">🕵️🕵️</span>
                    <span className="font-bold">جاسوسان</span>
                  </button>
                </div>
                
                {/* Minimum players warning */}
                <div className={`mt-3 p-3 rounded-xl text-sm ${
                  spyCount === 1 
                    ? 'bg-blue-500/20 border border-blue-500/30 text-blue-300' 
                    : 'bg-amber-500/20 border border-amber-500/30 text-amber-300'
                }`}>
                  <p className="flex items-center gap-2">
                    <span>{spyCount === 1 ? 'ℹ️' : '⚠️'}</span>
                    {spyCount === 1 
                      ? 'يجب أن يكون في اللعبة 3 لاعبين على الأقل'
                      : 'يجب أن يكون في اللعبة 4 لاعبين على الأقل • كل لاعب له صوتين!'
                    }
                  </p>
                </div>
              </div>

              {/* Category Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">تصنيف الكلمات</label>
                <div className="grid grid-cols-3 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        category === cat.id
                          ? 'border-game-gold bg-game-gold/20'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <span className="text-xl block mb-1">{cat.emoji}</span>
                      <span className="text-xs">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Info about max players */}
          <div className="mb-8 p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-sm text-white/60 flex items-center gap-2">
              <Users className="w-4 h-4" />
              الحد الأقصى للغرفة: 10 لاعبين
            </p>
          </div>

          {/* Error message */}
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-game-accent text-center mb-4"
            >
              {error}
            </motion.p>
          )}

          {/* Create button */}
          <button
            onClick={handleCreate}
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                جاري الإنشاء...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                إنشاء الغرفة
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
